/**
 * Custom CKEditor Commands for Font Styling
 * 
 * This module provides custom commands for handling font-related attributes 
 * in CKEditor, specifically for applying font styles and background colors.
 * 
 * ## Overview
 * - `CustomFontCommand`: A base class for handling font-related attributes.
 * - `CustomFontBackgroundColorCommand`: Extends `CustomFontCommand` to handle
 *   background color attributes.
 * 
 * ### How it Works
 * 1. **Refresh Method**:
 *    - Checks the current selection in the editor.
 *    - Updates the value of the command based on the attribute applied to the selection.
 *    - Verifies if the attribute is enabled for the current selection.
 * 
 * 2. **Execute Method**:
 *    - Applies or removes the specified attribute (`attributeKey`) to/from the selected text.
 *    - Handles collapsed selections and ranges with valid schema attributes.
 *    - Supports batch operations for grouping multiple changes into a single undo step.
 * 
 * ### Usage
 * - The `CustomFontBackgroundColorCommand` is specifically configured to handle
 *   the `CustomFontBackgroundColor` attribute defined in the `CKPlugins` constants.
 * 
 * ### References
 * - CKEditor Custom Plugins Documentation:
 *   https://ckeditor.com/docs/ckeditor5/latest/framework/guides/creating-simple-plugin.html
 * - CKEditor Command API:
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_core_command-Command.html
 * 
 * @module CustomFontCommand
 * @exports CustomFontCommand
 * @exports CustomFontBackgroundColorCommand
 */

import { CKPlugins } from '../../constants/constants';

const { Command } = window.ClassicEditor;

export class CustomFontCommand extends Command {
    value;
    attributeKey;
    editor;
    isEnabled;

    constructor(editor, attributeKey) {
        super(editor);

        this.attributeKey = attributeKey;
    }

    refresh() {
        const model = this.editor.model;
        const doc = model.document;

        this.value = doc.selection.getAttribute(this.attributeKey);
        this.isEnabled = model.schema.checkAttributeInSelection(doc.selection, this.attributeKey);
    }

    execute(options: any = {}) {
        const model = this.editor.model;
        const document = model.document;
        const selection = document.selection;
        const value = options.value;
        const batch = options.batch;

        const updateAttribute = writer => {
            if (selection.isCollapsed) {
                if (value) {
                    writer.setSelectionAttribute(this.attributeKey, value);
                } else {
                    writer.removeSelectionAttribute(this.attributeKey);
                }
            } else {
                const ranges = model.schema.getValidRanges(selection.getRanges(), this.attributeKey);
                for (const range of ranges) {
                    if ( value ) {
                        writer.setAttribute(this.attributeKey, value, range);
                    } else {
                        writer.removeAttribute(this.attributeKey, range);
                    }
                }
            }
        };

        // In some scenarios, you may want to use a single undo step for multiple changes (e.g. in color picker).
        if (batch) {
            model.enqueueChange( batch, writer => {
                updateAttribute( writer );
            } );
        } else {
            model.change(writer => {
                updateAttribute(writer);
            });
        }
    }
}

export class CustomFontBackgroundColorCommand extends CustomFontCommand {
    constructor(editor) {
        super(editor, CKPlugins.CustomFontBackgroundColor);
    }
}
